<script setup lang="ts">
import { HomeKpiGrid, HomeQuickLinks, TraceIdAlert, useSession, type KpiCard, type QuickLink } from '@yc/ui-kit'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { getHomeStats, type HomeStats } from '../api/home'

const { me } = useSession()
const router = useRouter()

const loading = ref(false)
const error = ref<unknown>(null)

const greeting = () => {
  const h = new Date().getHours()
  if (h < 6) return '夜深了'
  if (h < 12) return '早上好'
  if (h < 14) return '中午好'
  if (h < 18) return '下午好'
  return '晚上好'
}

/**
 * 卡片标签：后端 JSON 字段 → 页面文案，
 * 与 internal/home/service.go 的 Stats 一一对应
 */
const defs: Array<{ key: keyof HomeStats; label: string }> = [
  { key: 'total_shipments', label: '运单总数' },
  { key: 'in_transit', label: '在途运单' },
  { key: 'arrived', label: '待签收' },
  { key: 'signed_today', label: '今日签收' },
  { key: 'open_exceptions', label: '未关异常' },
  { key: 'pending_receipts', label: '待提交回单' },
]

const cards = ref<KpiCard[]>(defs.map((d) => ({ key: d.key, label: d.label, value: '—' })))

const links: QuickLink[] = [
  { to: '/shipments', label: '运单管理', perm: 'pod:shipment' },
  { to: '/trace', label: '全程追溯', perm: 'pod:trace' },
  { to: '/receipts', label: '回单台账', perm: 'pod:receipt' },
  { to: '/exceptions', label: '签收异常', perm: 'pod:exception' },
]

onMounted(async () => {
  loading.value = true
  error.value = null
  try {
    const s = await getHomeStats()
    cards.value = defs.map((d) => ({ key: d.key, label: d.label, value: s[d.key] ?? '—' }))
  } catch (e) {
    error.value = e
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="yc-home">
    <header class="yc-home__hero">
      <h2 class="yc-home__greeting">{{ greeting() }}，{{ me?.name || me?.username }}</h2>
      <p class="yc-home__sub">
        {{ me?.tenant?.name }}（{{ me?.tenant?.code }}）· {{ me?.username }}
      </p>
    </header>

    <div v-if="error" class="yc-home__error">
      <TraceIdAlert :error="error" />
    </div>

    <HomeKpiGrid :cards="cards" :loading="loading" />

    <HomeQuickLinks :links="links" @navigate="router.push" />
  </div>
</template>

<style scoped lang="scss">
.yc-home {
  &__hero {
    margin-bottom: var(--yc-space-6);
  }

  &__greeting {
    margin: 0;
    font-size: var(--yc-font-2xl);
    font-weight: 600;
  }

  &__sub {
    margin: var(--yc-space-2) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }
}
</style>
